/** THE CHAT TAB on the player board — one public room per game night.
 *
 *  Reads its own feed every few seconds, the same way the board itself reads
 *  players, so a new message lands without a reload. A phone may always READ;
 *  it may SEND only with a Patron Points screen name on this card.
 *
 *  Every message carries a Report, and any player can mute another player for
 *  themselves in one tap and un-mute from the same place. Messages are the
 *  night's: they are gone when the night turns over.
 *
 *  The venue strip only FILTERS what is on screen — every message still goes
 *  to the one room. Emoji are ordinary text: the phone's own keyboard works,
 *  and the quick bar simply sends one as a message.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flag, MapPin, Send, VolumeX, Volume2 } from 'lucide-react';

const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const EVERY = 4000;
const NOTICE_KEY = 'goal_chat_notice_seen';
const QUICK = ['🍻', '🚨', '🔥', '👏', '👎'];
const HOME = '__home__';

const timeOf = (iso) => {
    try {
        return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (_e) { return ''; }
};

export const GoalBoardChat = ({ cardId, t, c, ty, onOpenPlayer, skin = {} }) => {
    const [data, setData] = useState(null);
    const [text, setText] = useState('');
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState('');
    const [place, setPlace] = useState('');
    const [noticeOpen, setNoticeOpen] = useState(
        () => (typeof window !== 'undefined'
            ? localStorage.getItem(NOTICE_KEY) !== '1' : false));
    const tail = useRef(null);
    const device = typeof window !== 'undefined'
        ? localStorage.getItem('patron_device_id') || '' : '';
    const base = `${ORIGIN}/api/goal-game/public/${cardId}/chat`;
    const card = skin.chatBubble || '#1E2638';
    const line = skin.border || '#2D3748';
    const meta = skin.meta || '#94A3B8';
    const accent = skin.accent || c.accent || '#E11D48';

    const load = useCallback(async () => {
        try {
            const res = await fetch(`${base}?device_id=${encodeURIComponent(device)}`);
            if (res.ok) setData(await res.json());
        } catch (_e) { /* a dropped read changes nothing on screen */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId, device]);

    useEffect(() => {
        load();
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') load();
        }, EVERY);
        return () => clearInterval(id);
    }, [load]);

    useEffect(() => {
        tail.current?.scrollIntoView({ block: 'end' });
    }, [data?.messages?.length]);

    const act = async (path, payload) => {
        setBusy(true);
        try {
            const res = await fetch(`${base}${path}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ device_id: device, ...payload }),
            });
            if (res.ok) { setData(await res.json()); return true; }
            const detail = (await res.json().catch(() => ({})))?.detail;
            setNote(detail === 'too_fast' ? t('goalGame.chatTooFast')
                : detail === 'muted_tonight' ? t('goalGame.chatMutedTonight')
                    : t('goalGame.chatFailed'));
            return false;
        } catch (_e) {
            setNote(t('goalGame.chatFailed'));
            return false;
        } finally { setBusy(false); }
    };

    const post = async (body) => {
        if (!body || busy) return;
        setNote('');
        if (await act('', { text: body })) {
            localStorage.setItem(NOTICE_KEY, '1');
            setNoticeOpen(false);
            return true;
        }
        return false;
    };

    const send = async (e) => {
        e?.preventDefault?.();
        if (await post(text.trim())) setText('');
    };

    const messages = data?.messages || [];
    const muted = new Set(data?.muted || []);
    const canSend = Boolean(data?.can_send);

    // The venues that are really in tonight's room, in the order they appear.
    const venues = useMemo(() => {
        const seen = [];
        let home = false;
        messages.forEach((m) => {
            const name = String(m.establishment || '').trim();
            if (!name) { home = true; return; }
            if (!seen.includes(name)) seen.push(name);
        });
        return [...seen.map((name) => [name, name]),
                ...(home ? [[HOME, t('goalGame.boardAtHome')]] : [])];
    }, [messages, t]);

    const onScreen = messages.filter((m) => {
        if (!place) return true;
        if (place === HOME) return !String(m.establishment || '').trim();
        return String(m.establishment || '').trim() === place;
    });

    const pill = (on) => ({
        backgroundColor: on ? accent : 'transparent',
        color: on ? '#ffffff' : meta,
        borderColor: on ? accent : line,
    });

    return (
        <div className="flex h-full flex-col" data-testid="lp-chat">
            {venues.length > 0 && (
                <div className="sticky top-0 z-10 -mx-1 mb-2 flex gap-1.5 overflow-x-auto px-1 py-1"
                     style={{ backgroundColor: skin.bg || c.bg }} data-testid="lp-chat-filter">
                    <button type="button" onClick={() => setPlace('')}
                            data-testid="lp-chat-filter-all"
                            className="shrink-0 rounded-full border px-3.5 py-2 text-xs font-semibold"
                            style={pill(!place)}>
                        {t('goalGame.boardFilterAll')}
                    </button>
                    {venues.map(([key, label]) => (
                        <button key={key} type="button" onClick={() => setPlace(key)}
                                data-testid={`lp-chat-filter-${key === HOME ? 'home' : 'venue'}`}
                                className="shrink-0 rounded-full border px-3.5 py-2 text-xs font-semibold"
                                style={pill(place === key)}>
                            {label}
                        </button>
                    ))}
                </div>
            )}

            <div className="min-h-[180px] space-y-2" data-testid="lp-chat-messages">
                {onScreen.length === 0 ? (
                    <p className="py-6 text-center text-sm" style={{ color: meta }}
                       data-testid="lp-chat-empty">
                        {t('goalGame.chatEmpty')}
                    </p>
                ) : onScreen.map((m) => (
                    <div key={m.id} className={`rounded-2xl px-3 py-2.5 ${m.mine ? 'border-2' : 'border'}`}
                         style={{ backgroundColor: card,
                                  borderColor: m.mine ? accent : line }}
                         data-testid="lp-chat-row">
                        <div className="flex items-baseline justify-between gap-2">
                            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                <button type="button" onClick={() => onOpenPlayer?.(m.key)}
                                        className="truncate text-sm font-bold underline-offset-2 hover:underline"
                                        data-testid="lp-chat-name">
                                    {m.player}
                                </button>
                                <span className="flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold"
                                      style={{ borderColor: line, color: meta }}
                                      data-testid="lp-chat-place">
                                    <MapPin size={9} />
                                    {m.establishment || t('goalGame.boardAtHome')}
                                </span>
                                {m.mine && (
                                    <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold"
                                          style={{ backgroundColor: accent, color: '#ffffff' }}
                                          data-testid="lp-chat-you">
                                        {t('goalGame.boardYouTag')}
                                    </span>
                                )}
                            </div>
                            <span className="shrink-0 text-[11px] tabular-nums" style={{ color: meta }}
                                  data-testid="lp-chat-time">{timeOf(m.at)}</span>
                        </div>
                        <p className="mt-1 whitespace-pre-wrap break-words text-sm"
                           data-testid="lp-chat-text">{m.text}</p>
                        {!m.mine && (
                            <div className="mt-1.5 flex items-center gap-3">
                                <button type="button" disabled={busy || m.reported}
                                        onClick={() => act('/report', { message_id: m.id })}
                                        className="flex items-center gap-1 text-[11px] disabled:opacity-40"
                                        style={{ color: meta }}
                                        data-testid="lp-chat-report">
                                    <Flag size={11} />
                                    {m.reported ? t('goalGame.chatReported') : t('goalGame.chatReport')}
                                </button>
                                <button type="button" disabled={busy}
                                        onClick={() => act('/mute', { player_key: m.key,
                                                                      on: !muted.has(m.key) })}
                                        className="flex items-center gap-1 text-[11px]"
                                        style={{ color: meta }}
                                        data-testid="lp-chat-mute">
                                    {muted.has(m.key) ? <Volume2 size={11} /> : <VolumeX size={11} />}
                                    {muted.has(m.key) ? t('goalGame.chatUnmute') : t('goalGame.chatMute')}
                                </button>
                            </div>
                        )}
                    </div>
                ))}
                <div ref={tail} />
            </div>

            {muted.size > 0 && (
                <p className="mt-2 text-[11px]" style={{ color: meta }} data-testid="lp-chat-muted-hint">
                    {t('goalGame.chatMutedHint')}
                </p>
            )}

            {canSend && noticeOpen && (
                <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border px-3 py-2"
                     style={{ borderColor: line }}
                     data-testid="lp-chat-notice">
                    <span className="text-[11px]" style={{ color: meta }}>{t('goalGame.chatNotice')}</span>
                    <button type="button" data-testid="lp-chat-notice-ok"
                            onClick={() => { localStorage.setItem(NOTICE_KEY, '1'); setNoticeOpen(false); }}
                            className="shrink-0 rounded-full border px-3 py-1 text-[11px] font-semibold"
                            style={{ borderColor: line }}>
                        {t('goalGame.chatNoticeOk')}
                    </button>
                </div>
            )}

            {note && (
                <p className="mt-2 text-[11px] font-semibold text-amber-300" data-testid="lp-chat-note">
                    {note}
                </p>
            )}

            {canSend ? (
                <>
                    {/* One tap sends that emoji as its own message. The same
                        slow-send rule applies as for typed messages. */}
                    <div className="mt-3 flex items-center gap-1.5" data-testid="lp-chat-quick">
                        {QUICK.map((emoji) => (
                            <button key={emoji} type="button" disabled={busy}
                                    onClick={() => post(emoji)}
                                    data-testid={`lp-chat-quick-${emoji}`}
                                    className="flex h-11 w-11 items-center justify-center rounded-full border text-lg disabled:opacity-40"
                                    style={{ borderColor: line, backgroundColor: card }}>
                                {emoji}
                            </button>
                        ))}
                    </div>
                    <form onSubmit={send} className="mt-2 flex items-center gap-2" data-testid="lp-chat-form">
                        <input value={text} onChange={(e) => setText(e.target.value)}
                               maxLength={data?.max_chars || 240}
                               placeholder={t('goalGame.chatPlaceholder')}
                               data-testid="lp-chat-input"
                               className="min-h-[44px] min-w-0 flex-1 rounded-full border px-4 py-2.5 text-sm outline-none placeholder:opacity-50"
                               style={{ color: skin.text || c.text, backgroundColor: card,
                                        borderColor: line }} />
                        <button type="submit" disabled={busy || !text.trim()}
                                data-testid="lp-chat-send"
                                className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full px-4 text-sm font-semibold disabled:opacity-40"
                                style={{ backgroundColor: accent, color: '#ffffff' }}>
                            <Send size={14} />{t('goalGame.chatSend')}
                        </button>
                    </form>
                </>
            ) : (
                <p className="mt-3 rounded-xl border px-3 py-2 text-[11px]"
                   style={{ borderColor: line, color: meta }}
                   data-testid="lp-chat-readonly">
                    {data?.me?.player ? t('goalGame.chatMutedTonight') : t('goalGame.chatReadOnly')}
                </p>
            )}
        </div>
    );
};

export default GoalBoardChat;
